export function jsonObjectToFormData<T extends Record<string, unknown>>(
  jsonObject: T,
  formData = new FormData(),
  parentKey: string | null = null,
) {
  for (const key in jsonObject) {
    if (Object.hasOwn(jsonObject, key)) {
      const value = jsonObject[key];
      const currentKey = parentKey ? `${parentKey}[${key}]` : key;

      if (value instanceof File) {
        formData.append(currentKey, value);
      } else if (typeof value === 'object' && !Array.isArray(value)) {
        jsonObjectToFormData(value as Record<string, unknown>, formData, currentKey);
      } else if (Array.isArray(value)) {
        const isFile = value.some((field) => field instanceof File);
        // Detect and handle arrays of File objects
        if (value.length > 0 && isFile) {
          value.forEach((val, idx) => {
            formData.append(`${currentKey}[${idx}]`, val);
          });
        } else {
          // Handle other arrays
          value.forEach((item, index) => {
            jsonObjectToFormData({ [index]: item }, formData, currentKey);
          });
        }
      } else {
        formData.append(currentKey, value as string);
      }
    }
  }

  return formData;
}

export const getChangedFields = <T extends Record<string, any>>(
  currentData: T,
  originalData: T,
  dirtyFields?: Record<string, any>,
): Partial<T> => {
  const changes: Partial<T> = {};

  for (const key in currentData) {
    if (!Object.hasOwn(currentData, key)) continue;

    // Skip if dirtyFields is provided and this field is not dirty
    if (dirtyFields && !dirtyFields[key]) continue;

    const currentValue = currentData[key];
    const originalValue = originalData[key];

    const bothAreArrays = Array.isArray(currentValue) && Array.isArray(originalValue);
    const bothAreObjects = isObject(currentValue) && isObject(originalValue);
    if (bothAreArrays) {
      if (!arraysEqual(currentValue, originalValue)) {
        changes[key] = currentValue;
      }
    } else if (bothAreObjects) {
      const nestedChanges = getChangedFields(currentValue, originalValue);
      if (Object.keys(nestedChanges).length > 0) {
        changes[key] = currentValue;
      }
    } else if (currentValue !== originalValue) {
      changes[key] = currentValue;
    }
  }

  return changes;
};

export function isEmpty(value: any) {
  return ['', null, undefined].includes(value);
}

const isObject = (value: any): value is Record<string, any> => typeof value === 'object' && value !== null;

const arraysEqual = (a: any[], b: any[]): boolean => {
  if (a.length !== b.length) return false;

  return a.every((val, index) => {
    const other = b[index];
    if (isObject(val) && isObject(other)) {
      return Object.keys(getChangedFields(val, other)).length === 0;
    }
    return val === other;
  });
};
